import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Plus, Trash, PencilSimple, FloppyDisk, UploadSimple } from '@phosphor-icons/react';
import { API } from './adminConstants';
import { ShareLinkedInButton } from './ShareLinkedInButton';

export function CommunityManagement() {
  const { getAuthHeader } = useAuth();
  const [stories, setStories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editStory, setEditStory] = useState(null);
  const [form, setForm] = useState({ title: '', excerpt: '', content: '', date: '', author: '', image_url: '', slug: '', category: '' });
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = async () => { try { const r = await axios.get(`${API}/community`); setStories(r.data); } catch { toast.error('Failed to load'); } };
  useEffect(() => { load(); }, []);
  const resetForm = () => { setForm({ title: '', excerpt: '', content: '', date: '', author: '', image_url: '', slug: '', category: '' }); setEditStory(null); setShowForm(false); };

  // Suggest existing categories (datalist) and let admin enter new ones freely.
  const existingCategories = Array.from(new Set(stories.map(s => s.category).filter(Boolean))).sort();

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    const formData = new FormData(); formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/news/upload-image`, formData, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, image_url: res.data.image_url }));
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false); e.target.value = '';
  };

  const handleSave = async () => {
    if (!form.title || !form.excerpt) { toast.error('Title and excerpt required'); return; }
    const payload = { ...form, slug: form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 60), date: form.date || new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) };
    try {
      if (editStory) { await axios.put(`${API}/admin/community/${editStory.id}`, payload, { headers: getAuthHeader() }); toast.success('Updated'); }
      else { await axios.post(`${API}/admin/community`, payload, { headers: getAuthHeader() }); toast.success('Created'); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (story) => { setEditStory(story); setForm({ title: story.title || '', excerpt: story.excerpt || '', content: story.content || '', date: story.date || '', author: story.author || '', image_url: story.image_url || '', slug: story.slug || '', category: story.category || '' }); setShowForm(true); };
  const handleDelete = async (id) => { try { await axios.delete(`${API}/admin/community/${id}`, { headers: getAuthHeader() }); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };
  const filtered = stories.filter(s => `${s.title} ${s.excerpt}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid="community-management">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Community Stories</h1><p className="text-slate-500 text-sm">{stories.length} stories</p></div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-lime-600 hover:bg-lime-700" data-testid="create-community-btn"><Plus size={15} className="mr-1" />Add Story</Button>
      </div>
      <div className="mb-4"><Input placeholder="Search stories..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-72 border-slate-200" /></div>
      {showForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="community-form">
          <h3 className="font-semibold text-slate-900 mb-4">{editStory ? 'Edit Story' : 'New Community Story'}</h3>
          <div className="space-y-4">
            <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Story headline" data-testid="community-title-input" /></div>
            <div><Label className="text-xs">Excerpt / Summary *</Label><Textarea value={form.excerpt} onChange={e => setForm({ ...form, excerpt: e.target.value })} placeholder="Brief description..." rows={2} data-testid="community-excerpt-input" /></div>
            <div><Label className="text-xs">Full Article Content</Label><Textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} placeholder="Write the full story here..." rows={8} className="font-mono text-sm" data-testid="community-content-input" /><p className="text-[11px] text-slate-400 mt-1">Separate paragraphs with blank lines.</p></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs">Date</Label><Input value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} placeholder="e.g. 23/02/2026" /></div>
              <div><Label className="text-xs">Author</Label><Input value={form.author} onChange={e => setForm({ ...form, author: e.target.value })} placeholder="Author" /></div>
            </div>
            <div>
              <Label className="text-xs">Type / Category</Label>
              <Input
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
                placeholder="e.g. Charity Partnership, Sponsorship, Education, Event"
                list="community-category-list"
                data-testid="community-category-input"
              />
              {existingCategories.length > 0 && (
                <datalist id="community-category-list">
                  {existingCategories.map(c => <option key={c} value={c} />)}
                </datalist>
              )}
              <p className="text-[11px] text-slate-400 mt-1">Shown as a filter chip on the public Community page. Pick an existing type or create a new one.</p>
            </div>
            <div><Label className="text-xs">Image</Label>
              <div className="flex gap-3 items-start"><div className="flex-1"><Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="Paste URL or upload..." /></div>
                <label className="cursor-pointer shrink-0"><Button variant="outline" size="sm" asChild disabled={uploading}><span><UploadSimple size={15} className="mr-1" />{uploading ? 'Uploading...' : 'Upload Image'}</span></Button><input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} /></label>
              </div>
              {form.image_url && <div className="mt-2 w-40 h-24 rounded-lg overflow-hidden border border-slate-200"><img src={form.image_url} alt="Preview" className="w-full h-full object-cover" /></div>}
            </div>
            <div><Label className="text-xs">URL Slug (auto-generated if blank)</Label><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="e.g. my-story" /></div>
          </div>
          <div className="flex gap-3 mt-5"><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSave} className="bg-lime-600 hover:bg-lime-700" data-testid="save-community-btn"><FloppyDisk size={15} className="mr-1" />{editStory ? 'Update' : 'Publish'}</Button></div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="divide-y divide-slate-100">
          {filtered.map(story => (
            <div key={story.id} className="p-4 flex items-center gap-4" data-testid={`community-story-${story.id}`}>
              {story.image_url && <div className="w-20 h-14 rounded-lg overflow-hidden shrink-0 border border-slate-200"><img src={story.image_url} alt="" className="w-full h-full object-cover" /></div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-900 truncate">{story.title}</p>
                  {story.category && <span className="text-[10px] px-1.5 py-0.5 bg-lime-100 text-lime-700 rounded shrink-0">{story.category}</span>}
                  {story.content && <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded shrink-0">Full article</span>}
                </div>
                <div className="flex items-center gap-3 mt-0.5"><span className="text-xs text-slate-500">{story.date}</span>{story.author && <><span className="text-xs text-slate-300">|</span><span className="text-xs text-slate-500">{story.author}</span></>}</div>
                <p className="text-xs text-slate-400 mt-0.5 truncate">{story.excerpt}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <ShareLinkedInButton type="community" slug={story.slug} label="LinkedIn" />
                <Button variant="outline" size="sm" onClick={() => startEdit(story)}><PencilSimple size={14} className="mr-1" />Edit</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(story.id)} className="text-red-500 border-red-200 hover:bg-red-50"><Trash size={14} /></Button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="p-12 text-center text-slate-500">{search ? 'No matches' : 'No community stories yet.'}</div>}
        </div>
      </div>
    </div>
  );
}
